import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, Compass, AlertTriangle } from "lucide-react";

/**
 * Visualizador 360° (Street View) usando Pannellum (carregado via CDN no
 * index.html -> window.pannellum). Recebe um pano_id e monta a imagem
 * equiretangular servida pelo backend em /api/streetview/panorama.
 */
export default function StreetViewModal({ api, pano, thumbnail, onClose }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Dica "Arraste para explorar": aparece após o load e some na primeira
  // interação (mouse, toque, scroll ou teclado — desktop e mobile).
  const [showHint, setShowHint] = useState(false);
  const interactedRef = useRef(false);

  useEffect(() => {
    const eventos = ["pointerdown", "mousedown", "touchstart", "wheel", "keydown"];
    const hide = () => {
      interactedRef.current = true;
      setShowHint(false);
      eventos.forEach((ev) => window.removeEventListener(ev, hide, true));
    };
    eventos.forEach((ev) =>
      window.addEventListener(ev, hide, { capture: true, passive: true })
    );
    return () => eventos.forEach((ev) => window.removeEventListener(ev, hide, true));
  }, []);

  useEffect(() => {
    if (!pano?.pano_id) return;
    let cancelled = false;
    let tries = 0;

    const panoramaUrl = `${api}/streetview/panorama?pano_id=${encodeURIComponent(
      pano.pano_id
    )}&zoom=3`;

    function mount() {
      if (cancelled) return;
      const pannellum = window.pannellum;
      if (!pannellum || !containerRef.current) {
        tries += 1;
        if (tries > 80) {
          setError(true);
          setLoading(false);
          return;
        }
        setTimeout(mount, 100);
        return;
      }

      try {
        viewerRef.current = pannellum.viewer(containerRef.current, {
          type: "equirectangular",
          panorama: panoramaUrl,
          autoLoad: true,
          showZoomCtrl: false,
          showFullscreenCtrl: false,
          showControls: false,
          autoRotate: -2,
          autoRotateInactivityDelay: 3000,
          compass: false,
          hfov: 100,
          minHfov: 50,
          maxHfov: 120,
          backgroundColor: [4, 7, 15],
          crossOrigin: "anonymous",
        });

        viewerRef.current.on("load", () => {
          if (!cancelled) {
            setLoading(false);
            if (!interactedRef.current) setShowHint(true);
          }
        });
        viewerRef.current.on("error", () => {
          if (!cancelled) {
            setError(true);
            setLoading(false);
          }
        });
      } catch (e) {
        setError(true);
        setLoading(false);
      }
    }

    mount();

    return () => {
      cancelled = true;
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch (e) {
          /* noop */
        }
        viewerRef.current = null;
      }
    };
  }, [api, pano]);

  // ESC fecha
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[60] bg-[#04070f]"
      data-testid="streetview-modal"
    >
      {/* Panorama container */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Loading overlay -> usa a miniatura da fachada como fundo (sem texto) */}
      {loading && !error && (
        <div className="absolute inset-0 z-10 overflow-hidden bg-[#04070f]">
          {thumbnail && (
            <img
              src={thumbnail}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-xl"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#04070f] via-[#04070f]/40 to-[#04070f]/70" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-black/40 backdrop-blur-md">
              <Loader2 className="h-8 w-8 spin-slow text-teal-300" strokeWidth={1.6} />
            </div>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-[#04070f] px-6 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-300" strokeWidth={1.4} />
          <p className="font-display text-base font-semibold text-white">
            Não foi possível carregar a imagem 360°
          </p>
          <p className="max-w-sm text-sm text-slate-400">
            O Google pode não ter imagens deste ponto ou o serviço está
            temporariamente indisponível.
          </p>
          <button
            onClick={onClose}
            className="mt-2 rounded-xl bg-teal-400 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-teal-300"
          >
            Voltar ao globo
          </button>
        </div>
      )}

      {/* Top bar */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-3 bg-gradient-to-b from-black/70 to-transparent p-4 sm:p-6">
        <div className="glass pointer-events-auto flex min-w-0 items-center gap-2.5 rounded-2xl px-3.5 py-2.5 shadow-2xl shadow-black/40">
          <Compass className="h-5 w-5 shrink-0 text-teal-300" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">
              {pano?.label || "Street View"}
            </p>
            <p className="truncate text-xs text-slate-400">
              Visão 360°{pano?.date ? ` · ${pano.date}` : ""}
              {pano?.copyright ? ` · ${formatCopyright(pano.copyright)}` : ""}
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="glass pointer-events-auto grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-slate-200 shadow-2xl shadow-black/40 transition-all hover:scale-105 hover:text-rose-300"
          data-testid="streetview-close-btn"
          aria-label="Fechar Street View"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Hint — some na primeira interação (desktop e mobile) */}
      {!loading && !error && showHint && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center p-4 sm:p-6">
          <div className="glass rounded-full px-4 py-2 text-xs text-slate-300 shadow-lg shadow-black/40">
            Arraste para explorar
          </div>
        </div>
      )}
    </motion.div>
  );
}

function formatCopyright(c) {
  if (Array.isArray(c)) return c[0] || "";
  return String(c);
}
